import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes, FaFileDownload } from 'react-icons/fa';
import { profile } from '../../data/profile';

const SECTION_ITEMS = [
  { href: '#about', label: 'About' },
  { href: '#experience', label: 'Experience' },
  { href: '#featured-projects', label: 'Projects' },
  { href: '#contact', label: 'Contact' },
];

const ROUTE_ITEMS = [
  { path: '/blog', label: 'Blog' },
  { path: '/certificates', label: 'Certificates' },
  { path: '/social', label: 'Social' },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [active, setActive] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === '/';

  useEffect(() => {
    if (!onHome) {
      setScrolled(false);
      return;
    }
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
      for (const item of SECTION_ITEMS) {
        const el = document.querySelector(item.href);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 140 && rect.bottom >= 140) {
            setActive(item.href);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [onHome]);

  const goToSection = (href) => {
    if (onHome) {
      document.querySelector(href)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      navigate(`/${href}`);
    }
    setMenuOpen(false);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 bg-white text-black transition-shadow ${
        scrolled || !onHome ? 'shadow-[0_1px_0_0_rgba(0,0,0,0.1)] backdrop-blur' : ''
      }`}
    >
      <nav className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
        <Link to="/" className="flex items-center gap-2 font-black tracking-tight">
          <span className="flex h-8 w-8 items-center justify-center rounded-md border-2 border-black text-sm">
            PS
          </span>
          <span className="hidden sm:inline">{profile.name.split(' ')[0]}</span>
        </Link>

        <div className="hidden items-center gap-6 md:flex">
          {SECTION_ITEMS.map((item) => (
            <button
              key={item.href}
              onClick={() => goToSection(item.href)}
              className="relative py-1 text-sm font-medium text-black/70 transition-colors hover:text-black"
            >
              {item.label}
              {onHome && active === item.href && (
                <motion.span
                  layoutId="nav-underline"
                  className="absolute inset-x-0 -bottom-0.5 h-0.5 bg-black"
                />
              )}
            </button>
          ))}
          {ROUTE_ITEMS.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className="relative py-1 text-sm font-medium text-black/70 transition-colors hover:text-black"
            >
              {item.label}
              {location.pathname.startsWith(item.path) && (
                <motion.span
                  layoutId="nav-underline"
                  className="absolute inset-x-0 -bottom-0.5 h-0.5 bg-black"
                />
              )}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <a
            href={profile.resumeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden items-center gap-2 rounded-full border-2 border-black px-4 py-1.5 text-sm font-semibold transition-colors hover:bg-black hover:text-white sm:flex"
          >
            Resume <FaFileDownload size={12} />
          </a>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-md border-2 border-black md:hidden"
            aria-label="Toggle menu"
          >
            {menuOpen ? <FaTimes size={16} /> : <FaBars size={16} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t-2 border-black md:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {SECTION_ITEMS.map((item) => (
                <button
                  key={item.href}
                  onClick={() => goToSection(item.href)}
                  className="py-2 text-left text-base font-semibold"
                >
                  {item.label}
                </button>
              ))}
              {ROUTE_ITEMS.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMenuOpen(false)}
                  className="py-2 text-left text-base font-semibold"
                >
                  {item.label}
                </Link>
              ))}
              <a
                href={profile.resumeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 flex w-fit items-center gap-2 rounded-full border-2 border-black px-4 py-1.5 text-sm font-semibold"
              >
                Resume <FaFileDownload size={12} />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
